import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import { Book, Rocket, Code, HelpCircle, Wrench, Plug } from "lucide-react";
import { cn } from "../../lib/utils";

interface NavigationSection {
  title: string;
  slug: string;
  icon?: string;
  pages: Array<{
    title: string;
    slug: string;
  }>;
}

interface NavigationConfig {
  sections: NavigationSection[];
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Book,
  Rocket,
  Code,
  HelpCircle,
  Wrench,
  Plug,
};

export function DocsHome() {
  const [navigation, setNavigation] = useState<NavigationConfig | null>(null);

  useEffect(() => {
    fetch("/content/docs/navigation.json")
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load navigation");
        return res.json();
      })
      .then(setNavigation)
      .catch((err) => {
        console.error("Failed to load navigation:", err);
        setNavigation({ sections: [] });
      });
  }, []);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-12">
        <h1 className="su-text-page-title mb-4">
          Welcome to SendUpdates.ai Documentation
        </h1>
        <p className="su-text-subtitle text-lg">
          Everything you need to know about using SendUpdates.ai to streamline
          your investor updates and communications.
        </p>
      </div>

      {navigation && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {navigation.sections.map((section) => {
            const IconComponent =
              section.icon && iconMap[section.icon]
                ? iconMap[section.icon]
                : Book;

            return (
              <Link
                key={section.slug}
                to={`/docs/${section.slug}`}
                className="group"
              >
                <Card className="h-full border-[var(--su-border-primary)] bg-[var(--su-bg-secondary)] hover:border-[var(--su-purple-accent)] transition-colors rounded-none">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <IconComponent className="w-6 h-6 text-[var(--su-purple-accent)]" />
                      <CardTitle className="text-[var(--su-text-primary)] group-hover:text-[var(--su-purple-accent)] transition-colors">
                        {section.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[var(--su-text-muted)] text-sm mb-4">
                      {section.pages.length} article
                      {section.pages.length !== 1 ? "s" : ""}
                    </p>
                    <ul className="space-y-2">
                      {section.pages.slice(0, 3).map((page) => (
                        <li
                          key={page.slug}
                          className="text-sm text-[var(--su-text-secondary)] group-hover:text-[var(--su-text-primary)] transition-colors"
                        >
                          {page.title}
                        </li>
                      ))}
                      {section.pages.length > 3 && (
                        <li className="text-sm text-[var(--su-text-muted)]">
                          +{section.pages.length - 3} more
                        </li>
                      )}
                    </ul>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
